import React, { useEffect } from 'react';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { connect, ConnectedProps } from 'react-redux';
import { ProductType } from '@tink/core/src/types';
import { LoginMachineContext } from '../../../../connectivity/machines/loginMachine';
import { GradualReleaseLabels } from '../../../../utilities/useGradualRolloutTypes';
import {
  UseGradualRollout,
  useGradualRollout as useGradualRolloutImport,
} from '../../../../utilities/useGradualRollout';
import { useSelectedAuthenticationOptions } from '../../../../payments/hooks/useSelectedAuthenticationOptions/useSelectedAuthenticationOptions';
import { useAnalytics } from '../../../../core/Analytics/AnalyticsProvider';
import { addUserCredentials as addUserCredentialsConnect } from '../../../../stores/user-credentials-store';
import { setApplicationError as setApplicationErrorConnect } from '../../../../stores/app-state-store';
import { RefreshableItem } from '../../../../models/refreshableItem';
import { AppState } from '../../../../stores/redux-store';
import { setCredentials as setCredentialsConnect } from '../../../../stores/credentials-store';
import { UserEntity } from '../../../../models/user';
import { ProviderBanner } from '../../SubmitCredentials/molecules/ProviderBanner/ProviderBanner';
import { LegalDisclaimer } from '../../../molecules/LegalDisclaimer/LegalDisclaimer';
import { PageView } from '../../../../models/analytics';
import { Provider, ProviderEntity } from '../../../../models/provider';
import { useProduct } from '../../../ApplicationProviders/ProductProvider/ProductProvider';
import { useConfiguration } from '../../../ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { useCredentialsPoller } from '../../../hooks/useCredentialsPoller/useCredentialsPoller';
import {
  isUpdateConsent,
  ProductDimension,
  ProductDimensionCredentials,
} from '../../../../models/product';
import { getProviderRefreshableItems } from '../../../../utilities/provider-utils';
import { applicationParameters } from '../../../../utilities/application-parameters';
import {
  LegacySubmitCredentialsScreen,
  SubmitCredentialsScreenOwnProps,
} from './LegacySubmitCredentialsScreen';
import { ProviderRedirectInstructionsScreen } from './screens/ProviderRedirectInstructionsScreen/ProviderRedirectInstructionsScreen';
import { LoginMachineResponse, LoginMachineView } from './LoginMachineView';

/**
 * Exported only for testing purpose.
 * Not intended to be reused outside of SubmitCredentialsScreen
 */
export const getCredentialsMachineOperation = (
  productType: ProductType,
  productDimension: ProductDimension
): LoginMachineContext['operation'] => {
  if (
    productType === ProductType.CREDENTIALS &&
    productDimension === ProductDimensionCredentials.AUTHENTICATE
  ) {
    return 'authenticateExistingCredentials';
  }

  if (
    productType === ProductType.CREDENTIALS &&
    productDimension === ProductDimensionCredentials.REFRESH
  ) {
    return 'refreshExistingCredentials';
  }

  if (
    productType === ProductType.CREDENTIALS &&
    productDimension === ProductDimensionCredentials.ADD
  ) {
    return 'addExistingCredentials';
  }

  if (isUpdateConsent(productType, productDimension)) {
    return 'updateConsent';
  }

  return 'createCredentials';
};

/**
 * Exported only for testing purpose. Apply for both the type and function.
 * Not intended to be reused outside of SubmitCredentialsScreen
 */
export type GetRefreshableItemsParamsArg = {
  scope: string;
  refreshableItems: RefreshableItem[];
};

export const getRefreshableItems = (
  productType: ProductType,
  productDimension: ProductDimension,
  provider: ProviderEntity,
  params: GetRefreshableItemsParamsArg,
  calculateRefreshableItemsByProviderAndScope = getProviderRefreshableItems
): LoginMachineContext['refreshableItems'] => {
  if (
    productType === ProductType.CREDENTIALS &&
    productDimension === ProductDimensionCredentials.REFRESH
  ) {
    return [];
  }

  if (isUpdateConsent(productType, productDimension)) {
    return params.refreshableItems;
  }

  return calculateRefreshableItemsByProviderAndScope({
    provider,
    productType,
    scope: params.scope,
    refreshableItems: params.refreshableItems,
  });
};

type SubmitCredentialsScreenProps = Pick<
  SubmitCredentialsScreenOwnProps,
  'onSubmitCredentials' | 'onBack' | 'userHintId'
> & {
  provider: ProviderEntity;
  user?: UserEntity;
  renderProviderFields: boolean;
  refreshableItems: RefreshableItem[];
  useGradualRollout?: UseGradualRollout;
} & PropsFromRedux;

const SubmitCredentialsScreenPresenter: React.FC<SubmitCredentialsScreenProps> = ({
  session,
  credentials,
  userCredentials,
  addUserCredentials,
  oauthDescribe,
  provider,
  refreshableItems,
  onBack,
  onSubmitCredentials,
  userHintId,
  renderProviderFields,
  user,
  setCredentials,
  setApplicationError,
  useGradualRollout = useGradualRolloutImport,
}) => {
  const { tinklinkAnalytics } = useAnalytics();
  const { productType, productNames, productDimension } = useProduct();
  const { data: configurationData } = useConfiguration();
  const { selectedAuthenticationOptions } = useSelectedAuthenticationOptions(provider);
  const { signCredentials } = useCredentialsPoller();
  const callbackUri = applicationParameters.get('callbackUri');

  const { isInTestGroup: isInLoginMachineRefreshTestGroup } = useGradualRollout(
    PageView.SUBMIT_CREDENTIALS_SCREEN,
    GradualReleaseLabels.LOGIN_MACHINE_REFRESH
  );
  const { isInTestGroup: isLoginMachineUpdateConsentTestGroup } = useGradualRollout(
    PageView.SUBMIT_CREDENTIALS_SCREEN,
    GradualReleaseLabels.LOGIN_MACHINE_UPDATE_CONSENT
  );
  const testPayload = {
    userId: user?.id || '',
    provider,
    productType,
    productDimension,
  };
  const isLoginMachineRefreshTest = isInLoginMachineRefreshTestGroup(testPayload);
  const isLoginMachineUpdateConsentTest = isLoginMachineUpdateConsentTestGroup(testPayload);

  const isSparkasseProvider = provider.name.match('de-sparkasse.*?-ob');

  const showProviderInstructions =
    !isSparkasseProvider &&
    userAgent.isDesktop &&
    Provider.isRedirectAuthenticationFlow(provider) &&
    !Provider.hasFields(provider);

  useEffect(() => {
    tinklinkAnalytics.sendViewEvent({
      view: PageView.SUBMIT_CREDENTIALS_SCREEN,
    });
  }, [tinklinkAnalytics]);

  if (showProviderInstructions) {
    return <ProviderRedirectInstructionsScreen onSubmit={onSubmitCredentials} onBack={onBack} />;
  }

  if (isLoginMachineRefreshTest || isLoginMachineUpdateConsentTest) {
    const onDone = (response: LoginMachineResponse) => {
      switch (response.type) {
        case 'CREDENTIALS_CREATED':
          setCredentials(response.credentials);
          addUserCredentials(response.credentials);
          signCredentials(response.credentials.id);
          break;
        case 'CREDENTIALS_REFRESHED':
        case 'CREDENTIALS_UPDATED':
        case 'EXISTING_CREDENTIALS_AUTHENTICATED':
        case 'EXISTING_CREDENTIALS_REFRESHED':
          setCredentials(response.credentials);
          signCredentials(response.credentials.id);
          break;
        case 'UPDATED_CONSENT':
          signCredentials(response.credentials.id);
          break;
        case 'ERROR':
          setApplicationError(response.error);
          break;
        case 'BACK':
          onBack?.();
          break;
        default:
          break;
      }
    };

    return (
      <LoginMachineView
        session={session}
        userHintId={userHintId}
        renderProviderFields={renderProviderFields}
        provider={provider}
        operation={getCredentialsMachineOperation(productType, productDimension)}
        productNames={productNames}
        configurationData={configurationData}
        userId={user?.id || ''}
        selectedAuthenticationOptions={selectedAuthenticationOptions}
        credentials={credentials}
        userCredentials={userCredentials}
        refreshableItems={getRefreshableItems(productType, productDimension, provider, {
          scope: applicationParameters.get('scope') || '',
          refreshableItems:
            refreshableItems.length > 0
              ? refreshableItems
              : applicationParameters.get('refreshableItems'),
        })}
        credentialsId={applicationParameters.get('credentialsId')}
        oauthDescribe={oauthDescribe}
        onDone={onDone}
        callbackUri={callbackUri}
      />
    );
  }

  return (
    <LegacySubmitCredentialsScreen
      provider={provider}
      userHintId={userHintId}
      providerFieldsFallback={<ProviderBanner provider={provider} userHintId={userHintId} />}
      renderProviderFields={renderProviderFields}
      onBack={onBack}
      onSubmitCredentials={onSubmitCredentials}
      legalDisclaimer={
        <LegalDisclaimer provider={provider} pageView={PageView.SUBMIT_CREDENTIALS_SCREEN} />
      }
    />
  );
};

const mapDispatchToProps = {
  setCredentials: setCredentialsConnect,
  addUserCredentials: addUserCredentialsConnect,
  setApplicationError: setApplicationErrorConnect,
};

const mapStateToProps = (state: AppState) => ({
  session: state.session.data,
  credentials: state.credentials.data,
  userCredentials: state.userCredentials.data,
  oauthDescribe: state.oauth.description,
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const SubmitCredentialsScreen = connector(SubmitCredentialsScreenPresenter);
